import { useCallback, useEffect, useState } from "react";
import { Button } from "../../controls";
import { usePersistStore, useStore } from "../../store";
import { TrustapUserService } from "../../services";
import {
  useMyTrustapAccount,
  useTrustapConnectionHealth,
} from "../../hooks/queries";
import { v4 as uuidv4 } from "uuid";
import Utils from "../../utils/utils";
import DisconnectTrustapAccountPopup from "../../components/DisconnectTrustapAccountPopup";

function TrustapAccount() {
  const { token } = usePersistStore((s: any) => s);
  const { setIsLoading, setErrorMessage } = useStore((s: any) => s);
  const [showDisconnectPopup, setShowDisconnectPopup] = useState(false);

  const { data: trustapAccount, error, refetch } = useMyTrustapAccount(token);
  const { data: connectionHealth } = useTrustapConnectionHealth(token);

  // A connected seller whose grant predates a required scope: they can't ship
  // or claim until they reconnect. `scopesUpToDate === false` is only ever set
  // for full accounts, so this is false for guests/unconnected sellers.
  const needsReconnect = connectionHealth?.scopesUpToDate === false;

  useEffect(() => {
    if (error) setErrorMessage((error as Error).message);
  }, [error, setErrorMessage]);

  const handleGetStarted = useCallback(async () => {
    // Reserve the OAuth window synchronously while we're still inside the
    // user gesture. If we `await` first, popup blockers will kill it.
    const authWindow = window.open("about:blank", "_blank");
    if (!authWindow) {
      setErrorMessage(
        "Your browser blocked the Trustap window. Please allow popups for this site and try again."
      );
      return;
    }

    try {
      if (!trustapAccount) {
        setIsLoading(true);
        let ipAddress = "";
        try {
          ipAddress = await Utils.getIp();
        } catch {
          // Don't block account creation on IP lookup failure — the backend
          // has its own fallback. Just log and proceed.
          console.warn("Failed to resolve client IP for Trustap onboarding.");
        }
        const result = await TrustapUserService.createGuestAccount(token, {
          ipAddress,
        });
        if (result.error) {
          setErrorMessage(result.error);
          setIsLoading(false);
          authWindow.close();
          return;
        }
        setIsLoading(false);
      }

      setIsLoading(true);
      const state = uuidv4();
      const result = await TrustapUserService.setStateToken(token, {
        stateToken: state,
      });
      if (result.error) {
        setErrorMessage(result.error);
        setIsLoading(false);
        authWindow.close();
        return;
      }

      setIsLoading(false);
      const clientId = process.env.REACT_APP_TRUSTAP_CLIENT_ID;
      const redirectUri = encodeURIComponent(
        "https://bolle-app.vercel.app/dashboard/trustap-account-callback"
      );

      // Each Trustap basic_tx endpoint we call on behalf of a user via
      // the API-key + Trustap-User pattern requires the matching offline
      // scope to be granted at OAuth time:
      //   create_join   → /me/transactions/create_with_guest_user
      //   accept_payment→ /transactions/{id}/accept_payment
      //   cancel        → /transactions/{id}/cancel
      //   track         → /transactions/{id}/track          (Start Shipping)
      //   claim         → /transactions/{id}/claim_for_seller (Claim Transaction)
      // Missing the scope returns 401 'unauthorized' even when the API
      // key + seller_id are correct — see Trustap docs on offline access.
      const scope = encodeURIComponent(
        [
          "openid",
          "basic_tx:offline_create_join",
          "basic_tx:offline_accept_payment",
          "basic_tx:offline_cancel",
          "basic_tx:offline_track",
          "basic_tx:offline_claim",
        ].join(" ")
      );

      const realm =
        process.env.REACT_APP_TRUSTAP_IS_PRODUCTION === "production"
          ? "trustap"
          : "trustap-stage";

      const trustapAuthUrl = `https://sso.trustap.com/auth/realms/${realm}/protocol/openid-connect/auth?client_id=${clientId}&redirect_uri=${redirectUri}&response_type=code&scope=${scope}&state=${state}`;

      authWindow.location.href = trustapAuthUrl;
    } catch (ex: any) {
      setErrorMessage(ex?.message || "Unexpected error. Please try again.");
      setIsLoading(false);
      authWindow.close();
    }
  }, [trustapAccount, setIsLoading, setErrorMessage, token]);

  const handleDisconnect = useCallback(async () => {
    if (!token) return;
    setShowDisconnectPopup(false);
    setIsLoading(true);
    const result = await TrustapUserService.unlink(token);
    setIsLoading(false);
    if (result.error) {
      setErrorMessage(result.error);
      return;
    }
    refetch();
  }, [token, setIsLoading, setErrorMessage, refetch]);

  const handleCompleteProfile = useCallback(() => {
    const clientId = process.env.REACT_APP_TRUSTAP_CLIENT_ID;
    if (!clientId) return;
    const url =
      process.env.REACT_APP_TRUSTAP_IS_PRODUCTION === "production"
        ? `https://app.trustap.com/profile/payout/personal?edit=true&client_id=${clientId}`
        : `https://app.stage.trustap.com/profile/payout/personal?edit=true&client_id=${clientId}`;

    const w = window.open(url, "_blank");
    if (!w) {
      setErrorMessage(
        "Your browser blocked the Trustap window. Please allow popups and try again."
      );
    }
  }, [setErrorMessage]);

  return (
    <>
      <DisconnectTrustapAccountPopup
        show={showDisconnectPopup}
        onCancel={() => setShowDisconnectPopup(false)}
        onConfirm={handleDisconnect}
      />
      <div className="bg-pink p-4">
        <div className="row">
          <div className="col-12">
            <div className="chartCard py-4">
              <div className="tableCardHeader px-4">
                <div className="tableCardHeader__titleBlock">
                  <h2 className="tableCardHeader__title m-0">
                    My Trustap account
                  </h2>
                  <p className="tableCardHeader__subtitle m-0">
                    Required to receive payouts from your sales
                  </p>
                </div>
              </div>

              {!trustapAccount && (
                <div
                  className="alert alert-warning d-flex align-items-start gap-2 mx-4 p-3"
                  role="alert"
                >
                  <i
                    className="bi bi-exclamation-triangle-fill mt-1"
                    aria-hidden="true"
                  />
                  <div>
                    <strong>You can't claim transactions yet.</strong> Claiming
                    a payout requires a <strong>full</strong> Trustap account
                    (a guest account is not enough). Sales you make will sit in
                    Trustap until your full account is set up.
                  </div>
                </div>
              )}

              {trustapAccount && needsReconnect && (
                <div
                  className="alert alert-warning d-flex align-items-start gap-2 mx-4 p-3"
                  role="alert"
                >
                  <i
                    className="bi bi-exclamation-triangle-fill mt-1"
                    aria-hidden="true"
                  />
                  <div>
                    <strong>Reconnect your Trustap account.</strong> Your
                    connection is missing permissions we now need to start
                    shipping and release your payouts — until you reconnect,
                    those actions will fail. Disconnect below and connect again
                    to fix it (your existing sales are unaffected).
                    <div className="formMain mt-2 mb-0">
                      <Button
                        title="Reconnect now"
                        onClick={handleGetStarted}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="trustapCard">
                {!trustapAccount ? (
                  <>
                    <div className="trustapCard__iconWrap trustapCard__iconWrap--pending">
                      <i className="bi bi-shield-lock" aria-hidden="true"></i>
                    </div>
                    <h3 className="trustapCard__heading">
                      Set up your Trustap account
                    </h3>
                    <p className="trustapCard__text">
                      You haven't connected a Trustap account yet. Create one to
                      start selling your artworks — it's required to receive
                      payouts from your sales.
                    </p>
                    <div className="trustapCard__actions formMain mt-0">
                      <Button
                        title="Get started"
                        onClick={handleGetStarted}
                      />
                    </div>
                  </>
                ) : (
                  <>
                    <div className="trustapCard__iconWrap trustapCard__iconWrap--done">
                      <i
                        className="bi bi-check-circle-fill"
                        aria-hidden="true"
                      ></i>
                    </div>
                    <h3 className="trustapCard__heading">
                      Trustap account connected
                    </h3>
                    <p className="trustapCard__text">
                      Your Trustap account is ready. Head to Trustap to finish
                      your payout profile so we can release your earnings
                      without delays.
                    </p>
                    <p className="trustapCard__subtext">
                      Already completed your Trustap profile? You can skip this
                      step.
                    </p>
                    <div className="trustapCard__actions formMain mt-0">
                      <Button
                        title="Go to Trustap profile"
                        onClick={handleCompleteProfile}
                      />
                    </div>
                    <button
                      type="button"
                      className="btn btn-link btn-sm trustapCard__disconnect"
                      onClick={() => setShowDisconnectPopup(true)}
                    >
                      Disconnect Trustap account
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default TrustapAccount;
